import { cn, Pill, PillProps, Stack } from 'component-library';
import { FC, HTMLAttributes, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { BenchmarkType, PlotReportFactLabelNameEnum, ValueClass } from '@/api/rest/resources/types/fact';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { isNullish } from '@/utils/booleans';
import { printQuarter, printYear } from '@/utils/formatting/date';

import { ValueClassPill } from '../Pill/ValueClassPill';
import { Chart, ComposedChart, Line, ReferenceArea, ReferenceLine, TooltipProps } from './components/Chart';
import { CHART_ZOOM, ChartZoomStateHook } from './components/ChartZoom';
import { ChartLegendBar } from './components/LegendBar';
import { ChartTooltip, ChartTooltipTextPrimary, ChartTooltipTextSecondary } from './components/Tooltip';
import { ChartLegendBarItem } from './components/types';
import { useYAxisDomainFromMouseWheelZoom } from './hooks/useYAxisDomainFromMouseWheelZoom';
import { NoChartData } from './NoChartData';
import { GraphFact } from './types';
import { getChartDataFromGraphFact, sortChartData } from './utils';
import { getChartZoomedDomain } from './utils/getChartZoomedDomain';

const CHART_COLORS = {
  observed: '#1a1a1a',
  seasonalAverage: '#8c8c8c',
  forecast: '#6a9f4b',
} as const;

export type LstBenchmarkClass = BenchmarkType.high | BenchmarkType.moderate | BenchmarkType.low;

export const LST_BENCHMARK_COLORS: Record<LstBenchmarkClass, { primary: string; text: string }> = {
  [BenchmarkType.high]: { primary: '#d9542b', text: '#ffffff' },
  [BenchmarkType.moderate]: { primary: 'rgba(0, 0, 0, 0.1)', text: 'rgba(0, 0, 0, 0.6)' },
  [BenchmarkType.low]: { primary: '#5bc0de', text: '#ffffff' },
};

const getLstBenchmarkColors = (benchmark: LstBenchmarkClass) => LST_BENCHMARK_COLORS[`${benchmark}`];

export const LstBenchmarkPill = ({
  benchmarkType,
  className,
  ...delegated
}: PillProps & { benchmarkType: LstBenchmarkClass }) => {
  const { t } = useTranslation();
  const { primary, text } = getLstBenchmarkColors(benchmarkType);

  return (
    <Pill
      className={cn('typography-overline font-bold w-fit rounded px-[9px] py-2', className)}
      style={{ color: text, backgroundColor: primary }}
      {...delegated}
    >
      {t(`global.benchmark.${benchmarkType}`)}
    </Pill>
  );
};

const LINE_PROPS = { dot: false, isAnimationActive: false, connectNulls: true } as const;

const LABELS = 'shared.ncaDetail.details.landSurfaceTemperature.labels';
const BENCHMARK_STRIP_HEIGHT_PX = 34;
const LEGEND_ALIGNED_WITH_PLOT_CLASS = 'sm:ml-[55px]';
const LEGEND_ITEM_CLASS = 'bg-transparent p-0 pr-4';

const factNames = {
  lst: 'lst',
  lst_normal: 'lst_normal',
  lst_forecast: 'lst_forecast',
  lst_benchmark: 'lst_benchmark',
};

type SeriesPointValue = {
  value: number | null;
  labels?: { name: string; value: string }[];
};

type SeriesPoint = SeriesPointValue | null;

type ChartEntry = {
  label: number;
  lst?: SeriesPoint;
  lst_normal?: SeriesPoint;
  lst_forecast?: SeriesPoint;
  lst_benchmark?: SeriesPoint;
  benchmark?: LstBenchmarkClass;
};

const LST_BENCHMARK_CLASSES = Object.keys(LST_BENCHMARK_COLORS) as LstBenchmarkClass[];

export const getBenchmarkClass = (point?: Pick<GraphFact, 'labels'> | null) => {
  const benchmark = point?.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.benchmark)?.value;
  return LST_BENCHMARK_CLASSES.find((benchmarkClass) => benchmarkClass === benchmark);
};

const getQuarterStart = (quarterEnd: number) => {
  const date = new Date(quarterEnd);
  return Date.UTC(date.getUTCFullYear(), Math.floor(date.getUTCMonth() / 3) * 3, 1);
};

const getYAxisDomain = (entries: ChartEntry[]): [number, number] => {
  const values = entries.flatMap((entry) =>
    [entry.lst, entry.lst_normal, entry.lst_forecast].map((point) => point?.value),
  );
  const numbers = values.filter((value): value is number => typeof value === 'number');

  if (numbers.length === 0) return [0, 1];

  return [Math.floor(Math.min(...numbers)) - 2, Math.ceil(Math.max(...numbers)) + 2];
};

const getXAxisProps = (firstEntry: ChartEntry, lastEntry: ChartEntry) => {
  const firstYear = new Date(firstEntry.label).getFullYear();
  const lastYear = new Date(lastEntry.label).getFullYear();
  const domain: [number, number] = [new Date(firstYear, 0, 1).valueOf(), new Date(lastYear, 11, 31).valueOf()];

  return {
    dataKey: 'label',
    scale: 'time',
    domain,
    ticks: Array.from({ length: lastYear - firstYear + 1 }, (_, i) => new Date(firstYear + i, 6, 1).valueOf()),
    tickFormatter: printYear,
    tickMargin: 4,
  } as const;
};

export const getLstChartData = (data: GraphFact[]): ChartEntry[] => {
  const dateDataMap = getChartDataFromGraphFact(data, factNames);
  const entries = sortChartData(dateDataMap, (date) => new Date(date).getTime()) as unknown as ChartEntry[];

  return entries
    .filter((entry) => [entry.lst, entry.lst_normal, entry.lst_forecast].some((point) => !isNullish(point?.value)))
    .map((entry) => ({ ...entry, benchmark: getBenchmarkClass(entry.lst_benchmark) }));
};

export const LstHistoricalChart: FC<
  HTMLAttributes<HTMLDivElement> &
    Partial<ChartZoomStateHook> & {
      data: GraphFact[];
    }
> = ({ data, zoom, setZoom, ...delegated }) => {
  const { t } = useTranslation();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartData = getLstChartData(data);
  const defaultDomain = getYAxisDomain(chartData);
  const yAxisDomainFromMouseWheel = useYAxisDomainFromMouseWheelZoom(chartContainerRef, defaultDomain, setZoom);

  const firstEntry = chartData.at(0);
  const lastEntry = chartData.at(-1);

  if (!firstEntry || !lastEntry) {
    return (
      <div className='relative flex h-full w-full flex-col justify-center' {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const xAxisProps = getXAxisProps(firstEntry, lastEntry);
  const yAxisDomainFromButtons = getChartZoomedDomain({
    data: data.filter((fact) => fact.name !== factNames.lst_benchmark),
    zoom,
    defaultDomain,
    allowNegativeRange: true,
  });
  const yAxisDomain = zoom === CHART_ZOOM.MOUSE_WHEEL ? yAxisDomainFromMouseWheel : yAxisDomainFromButtons;

  const hasForecast = chartData.some((entry) => !isNullish(entry.lst_forecast?.value));
  const forecastStart = chartData.find((entry) => !isNullish(entry.lst_forecast?.value))?.label;
  const benchmarkEntries = chartData.filter((entry) => entry.benchmark && !isNullish(entry.lst?.value));

  return (
    <Stack spacing={4} className='w-full' data-testid='lst-chart' {...delegated}>
      <Chart.Container
        ref={chartContainerRef}
        data={chartData}
        config={{ lst: { label: t(`${LABELS}.observed`), color: CHART_COLORS.observed } }}
      >
        <ComposedChart data={chartData}>
          <Line
            {...LINE_PROPS}
            dataKey='lst_normal.value'
            stroke={CHART_COLORS.seasonalAverage}
            strokeDasharray='4 4'
            strokeWidth={1.5}
            className='lst-seasonal-average'
          />
          <Line
            {...LINE_PROPS}
            dataKey='lst.value'
            stroke={CHART_COLORS.observed}
            strokeWidth={2}
            className='lst-observed'
          />
          {hasForecast && (
            <Line
              {...LINE_PROPS}
              dataKey='lst_forecast.value'
              stroke={CHART_COLORS.forecast}
              strokeWidth={2}
              className='lst-forecast'
            />
          )}
          {!isNullish(forecastStart) && (
            <ReferenceLine x={forecastStart} stroke={CHART_COLORS.seasonalAverage} strokeDasharray='3 3' />
          )}
          <Chart.XAxis {...xAxisProps} interval='preserveStart' className='lst-x-axis' />
          <Chart.YAxis domain={yAxisDomain} allowDataOverflow className='lst-y-axis'>
            <Chart.Label>{t('global.ui.unit.°C')}</Chart.Label>
          </Chart.YAxis>
          <Chart.Tooltip content={<CustomTooltip />} />
        </ComposedChart>
      </Chart.Container>
      {benchmarkEntries.length > 0 && <BenchmarkStrip entries={benchmarkEntries} xAxisProps={xAxisProps} />}
      <CustomLegendBar hasBenchmark={benchmarkEntries.length > 0} hasForecast={hasForecast} />
    </Stack>
  );
};

const renderStripSegment = (key: string | number, x1: number, x2: number, fill: string) => (
  <ReferenceArea
    key={key}
    x1={x1}
    x2={x2}
    y1={0}
    y2={1}
    fill={fill}
    fillOpacity={1}
    stroke='none'
    ifOverflow='hidden'
  />
);

const BenchmarkStrip = ({
  entries,
  xAxisProps,
}: {
  entries: ChartEntry[];
  xAxisProps: ReturnType<typeof getXAxisProps>;
}) => {
  const [domainStart, domainEnd] = xAxisProps.domain;
  const segments = entries.flatMap(({ label, benchmark }) =>
    benchmark && benchmark !== BenchmarkType.moderate ? [{ label, benchmark }] : [],
  );

  return (
    <Chart.Container
      data={entries}
      config={{}}
      height={BENCHMARK_STRIP_HEIGHT_PX}
      className='-mt-3'
      data-testid='lst-benchmark-strip'
    >
      <ComposedChart data={entries}>
        {renderStripSegment('track', domainStart, domainEnd, LST_BENCHMARK_COLORS.moderate.primary)}
        {segments.map(({ label, benchmark }) =>
          renderStripSegment(label, getQuarterStart(label), label, getLstBenchmarkColors(benchmark).primary),
        )}
        <Chart.XAxis {...xAxisProps} hide />
        <Chart.YAxis domain={[0, 1]} tick={false} axisLine={false} padding={{ top: 0 }} />
      </ComposedChart>
    </Chart.Container>
  );
};

const DashedLineShape = () => (
  <div className='w-3 border-t-2 border-dashed' style={{ borderColor: CHART_COLORS.seasonalAverage }} />
);

const ColorSquare = ({ color }: { color: string }) => (
  <div className='h-3 w-3 rounded-sm' style={{ backgroundColor: color }} />
);

const CustomLegendBar = ({ hasBenchmark, hasForecast }: { hasBenchmark: boolean; hasForecast: boolean }) => {
  const { t } = useTranslation();

  const benchmarkItems: ChartLegendBarItem[] = hasBenchmark
    ? [
        { label: t(`${LABELS}.aboveRange`), color: LST_BENCHMARK_COLORS.high.primary },
        { label: t(`${LABELS}.withinRange`), color: LST_BENCHMARK_COLORS.moderate.primary },
        { label: t(`${LABELS}.belowRange`), color: LST_BENCHMARK_COLORS.low.primary },
      ].map((item) => ({ ...item, shape: <ColorSquare color={item.color} /> }))
    : [];

  const legendBarItems: ChartLegendBarItem[] = [
    ...benchmarkItems,
    { label: t(`${LABELS}.observed`), color: CHART_COLORS.observed, shape: 'line' },
    { label: t(`${LABELS}.seasonalAverage`), color: CHART_COLORS.seasonalAverage, shape: <DashedLineShape /> },
    ...(hasForecast ? [{ label: t(`${LABELS}.forecast`), color: CHART_COLORS.forecast, shape: 'line' as const }] : []),
  ];

  return (
    <ChartLegendBar
      items={legendBarItems.map((item) => ({ ...item, className: LEGEND_ITEM_CLASS }))}
      className={LEGEND_ALIGNED_WITH_PLOT_CLASS}
      data-testid='lst-chart-legend'
    />
  );
};

const printTemperature = (value: number | null | undefined, unit: string) =>
  isNullish(value) ? null : `${getDisplayNumber(value, window.navigator.language)} ${unit}`;

const CustomTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();
  const unit = t('global.ui.unit.°C');

  const entry = payload?.[0]?.payload as ChartEntry | undefined;
  if (!active || !entry) return null;

  const isForecastOnly = isNullish(entry.lst?.value) && !isNullish(entry.lst_forecast?.value);
  const valueClass = entry.lst?.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.value_class)
    ?.value as ValueClass | undefined;

  const rows = [
    { label: t(`${LABELS}.observed`), value: printTemperature(entry.lst?.value, unit) },
    { label: t(`${LABELS}.seasonalAverage`), value: printTemperature(entry.lst_normal?.value, unit) },
    ...(isForecastOnly
      ? [{ label: t(`${LABELS}.forecast`), value: printTemperature(entry.lst_forecast?.value, unit) }]
      : []),
  ].filter((row) => row.value);

  return (
    <ChartTooltip data-testid='lst-chart-tooltip'>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{printQuarter(entry.label)}</ChartTooltipTextSecondary>
        {!!entry.benchmark && <LstBenchmarkPill benchmarkType={entry.benchmark} />}
        {!!valueClass && <ValueClassPill title={valueClass} />}
      </Stack>
      {rows.map((row) => (
        <Stack key={row.label} spacing={8} direction='row' className='justify-between'>
          <ChartTooltipTextPrimary>{row.label}</ChartTooltipTextPrimary>
          <ChartTooltipTextPrimary>{row.value}</ChartTooltipTextPrimary>
        </Stack>
      ))}
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipLstHistorical };
